import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import { apiClient } from '../utils/apiClient';

export interface Device {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface DevicesResponse {
  enabled: boolean;
  devices: Device[];
}

export interface RecoveryStatus {
  total: number;
  used: number;
  generatedAt: string | null;
}

class WebAuthnError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'WebAuthnError';
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await apiClient(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  return data as T;
}

export function isSupported(): boolean {
  return browserSupportsWebAuthn();
}

export async function completeRegistration(
  intentId: string,
  options: PublicKeyCredentialCreationOptionsJSON,
  deviceLabel?: string
): Promise<Record<string, unknown> & { recoveryCodes?: string[] }> {
  const attestation = await startRegistration({ optionsJSON: options });
  return postJson('/webauthn/register/complete', { intentId, attestation, deviceLabel });
}

export async function recoveryLogin(intentId: string, code: string): Promise<Record<string, unknown>> {
  return postJson('/webauthn/recovery/login', { intentId, code });
}

export async function getRecoveryStatus(): Promise<RecoveryStatus> {
  const res = await apiClient('/webauthn/recovery/status');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  return data;
}

export async function regenerateRecoveryCodes(): Promise<string[]> {
  const data = await postJson<{ codes: string[] }>('/webauthn/recovery/regenerate', {});
  return data.codes;
}

export async function completeAuthentication(
  intentId: string,
  options: PublicKeyCredentialRequestOptionsJSON
): Promise<Record<string, unknown>> {
  const assertion = await startAuthentication({ optionsJSON: options });
  return postJson('/webauthn/auth/complete', { intentId, assertion });
}

export async function listDevices(): Promise<DevicesResponse> {
  const res = await apiClient('/webauthn/devices');
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  return data;
}

export async function addDeviceAndPersist(deviceLabel?: string): Promise<void> {
  const res = await apiClient('/webauthn/devices/begin', { method: 'POST' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  await completeRegistration(data.intentId, data.options, deviceLabel);
}

export async function removeDevice(credentialId: string): Promise<void> {
  const res = await apiClient(`/webauthn/devices/${encodeURIComponent(credentialId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  }
}

export async function disable2FA(): Promise<void> {
  const res = await apiClient('/webauthn/disable', { method: 'POST' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new WebAuthnError(res.status, data.message ?? 'Erreur 2FA');
  }
}
